import { fmt, formatDay, withUnit } from '../view/format';
import { paintClass } from '../view/sections';

// Internal paint-reading areas ("front-left-door") → place on the top-view paint map (view/sections.js PAINT_PLACES) and side
const PAINT_AREAS = {
    hood: ['bonnet', 'Centre'],
    bonnet: ['bonnet', 'Centre'],
    roof: ['roof', 'Centre'],
    tailgate: ['tailgate', 'Centre'],
    trunk: ['tailgate', 'Centre'],
    'front-bumper': ['frontBumper', 'Centre'],
    'rear-bumper': ['rearBumper', 'Centre'],
    'front-left-fender': ['frontWing', 'Left'],
    'front-left-door': ['frontDoor', 'Left'],
    'rear-left-door': ['rearDoor', 'Left'],
    'rear-left-fender': ['rearWing', 'Left'],
    'front-right-fender': ['frontWing', 'Right'],
    'front-right-door': ['frontDoor', 'Right'],
    'rear-right-door': ['rearDoor', 'Right'],
    'rear-right-fender': ['rearWing', 'Right'],
};

const present = value => value != null && value !== '';
const field = (key, label, value, sub) =>
    present(value) ? { key, label, value: String(value), sub: sub || '' } : null;

const vehicleFields = (v, t) => {
    const s = v.specs || {};
    const power = [s.power_kw && withUnit(s.power_kw, 'kw'), s.power_hp && withUnit(s.power_hp, 'hp')]
        .filter(Boolean)
        .join(' / ');
    const engine = [
        s.cylinder_capacity && withUnit(fmt(s.cylinder_capacity), 'cm3'),
        s.fuel_type && t(`adapter.fuel.${s.fuel_type}`, { defaultValue: s.fuel_type }),
    ]
        .filter(Boolean)
        .join(', ');

    return [
        field('model', t('adapter.fields.model'), v.version || v.model),
        field(
            'bodyType',
            t('adapter.fields.bodyType'),
            s.body_type,
            s.doors ? t('adapter.doors', { count: s.doors }) : ''
        ),
        field('engineSize', t('adapter.fields.engine'), engine, power),
        field(
            'gearbox',
            t('adapter.fields.gearbox'),
            s.gear_box && t(`adapter.gearbox.${s.gear_box}`, { defaultValue: s.gear_box })
        ),
        field('colour', t('adapter.fields.colour'), s.color),
        field('euro', t('adapter.fields.euro'), s.euro_version),
        field('production', t('adapter.fields.productionYear'), s.production_date),
        field('registration', t('adapter.fields.registration'), v.registration_date),
        field(
            'mileage',
            t('adapter.fields.mileageAtInspection'),
            present(v.mileage_km) ? withUnit(fmt(v.mileage_km), 'km') : null
        ),
        field('owners', t('adapter.fields.owners'), v.ownersCount),
        field('keys', t('adapter.fields.keys'), s.keys_count),
    ].filter(Boolean);
};

const paintPanels = (readings, t) =>
    (readings || [])
        .filter(r => typeof r.value === 'number')
        .map(r => {
            const [pos, side] = PAINT_AREAS[r.area] || [null, ''];
            return {
                name: t(`adapter.paintAreas.${r.area}`, { defaultValue: r.area }),
                side: side && t(`adapter.sides.${side}`),
                // the untranslated side picks the panels shown on the map; `side` is only for display
                sideKey: side,
                pos,
                um: r.value,
            };
        });

// Systems checked by the inspector, then the tyres of each wheel set (tread depth and DOT as recorded)
const checks = (v, t) => {
    const systems = (v.verifiedSystems || []).map(s => {
        // the locale keys are upper case (WARNING, ERROR, CHECK), whatever case the inspection used
        const status = String(s.status).toUpperCase();

        return {
            group: t('adapter.checks.systems'),
            name: t(`adapter.systems.${s.system}`, { defaultValue: s.system }),
            status: status === 'OK' ? 'Pass' : 'Attention',
            note: status === 'OK' ? '' : t(`labels.systemStatus.${status}`, { defaultValue: s.status }),
        };
    });
    const tyres = (v.wheelSets || []).flatMap(set =>
        (set.tires || []).map(tire => ({
            group: set.name,
            name: t(`adapter.tyrePositions.${tire.position}`, { defaultValue: tire.position }),
            note: [
                present(tire.depth) && t('adapter.tread', { depth: tire.depth }),
                tire.dot && `DOT ${tire.dot}`,
                tire.type,
            ]
                .filter(Boolean)
                .join(' · '),
        }))
    );

    return [...systems, ...tyres];
};

// The inspection app records a damage's extent as the last part of its quantity key (small, medium, large);
// the report shows it on the Minor / Moderate / Major scale, and keeps any other value as it came
const SEVERITIES = { small: 'Minor', medium: 'Moderate', large: 'Major' };
// a count range keeps its numbers: "6-to-10" reads "6–10"
const severityOf = value => {
    const text = String(value || '');
    const range = text.match(/^(\d+)-to-(\d+)$/);
    if (range) return `${range[1]}–${range[2]}`;
    return SEVERITIES[text.toLowerCase()] || value || '';
};

// The inspection app names a damage's part and type, or sends its code ("front-right-fender", "upholstery-worn"). A
// code gets its label (damage types) or reads as words ("Front right fender"); any other text (the app's own name, a
// translation of the report) stays as written. SC-QA-119 staging check: the English page showed the codes as they came
const DAMAGE_CODE = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const codeAsWords = code => {
    const words = code.replace(/-/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
};
const damageLabel = (value, t, group) => {
    const text = String(value || '');
    if (!DAMAGE_CODE.test(text)) return text;
    return group ? t(`adapter.${group}.${text}`, { defaultValue: codeAsWords(text) }) : codeAsWords(text);
};

const damages = (v, t) => [
    ...(v.accidents || []).map(a => ({
        area: a.description || t('adapter.accident'),
        severity: 'Major',
        text: present(a.repairCost) ? t('adapter.repairCost', { cost: fmt(a.repairCost) }) : '',
        photo: (a.photos || [])[0],
    })),
    ...(v.exteriorDamages || []).map(d => ({
        area: damageLabel(d.part, t),
        severity: severityOf(d.severity),
        text: damageLabel(d.type, t, 'damageTypes'),
        photo: d.photo,
    })),
];

const documents = (v, t) => {
    const docs = v.documents || {};
    const pages = (docs.serviceBookPages || []).length;

    return [
        docs.registrationPaper && { name: t('adapter.documents.registration'), status: 'Checked' },
        (pages > 0 || v.serviceBookStatus) && {
            name: t('adapter.documents.serviceBook'),
            status: 'Checked',
            note: [v.serviceBookStatus, pages > 0 && t('adapter.documents.pages', { count: pages })]
                .filter(Boolean)
                .join(' · '),
        },
    ].filter(Boolean);
};

// the registration paper and service book pages as photographed, for the document gallery
const documentPhotos = (v, t) => {
    const docs = v.documents || {};
    return [
        docs.registrationPaper && { url: docs.registrationPaper, label: t('layout.registrationPaper') },
        ...(docs.serviceBookPages || []).map((url, i) => ({ url, label: t('layout.servicePage', { index: i + 1 }) })),
    ].filter(item => item && item.url);
};

// the asking or selling price, when the inspection recorded one
const pricing = v => {
    const p = v.pricing;
    if (!p || !(Number(p.priceGross) > 0)) return null;
    return {
        amount: `${p.currency ? `${p.currency} ` : ''}${fmt(p.priceGross)}`,
        city: p.locationCity || '',
        country: p.locationCountry || '',
    };
};

// "Yes<br />- Test drive completed:<br />-- Engine: no issues" → one line per note, markup removed
const driveNotes = notes =>
    String(notes || '')
        .split(/<br\s*\/?>|\n/i)
        .map(line =>
            line
                .replace(/<[^>]+>/g, '')
                .replace(/^[-\s]+/, '')
                .trim()
        )
        .filter(line => line && !/^(yes|no)$/i.test(line));

const tiles = (v, panels, damageCount, documentCount, t) => {
    const values = panels.map(p => p.um);
    const repainted = panels.filter(p => paintClass(p.um) !== 'ok').length;

    return [
        present(v.mileage_km) && {
            value: withUnit(fmt(v.mileage_km), 'km'),
            label: t('adapter.tiles.mileage'),
            href: '#checks',
        },
        values.length > 0 && {
            value: withUnit(`${Math.min(...values)}–${Math.max(...values)}`, 'um'),
            label: t('adapter.tiles.paintRange'),
            href: '#paint',
            small: true,
        },
        values.length > 0 && { value: String(repainted), label: t('adapter.tiles.repainted'), href: '#paint' },
        { value: String(damageCount), label: t('adapter.tiles.damages'), href: '#damages' },
        documentCount > 0 && { value: String(documentCount), label: t('adapter.tiles.documents'), href: '#documents' },
    ].filter(Boolean);
};

/**
 * Our own inspection (a1_cars) as a report view model: vehicle, tiles, status and the inspection sections.
 * `gallery` photos become the photo grid; paint readings are classed by thickness, not by the source's flag.
 */
export const fromInternalVehicle = (v, t, { standalone = false } = {}) => {
    if (!v) return null;

    const panels = paintPanels(v.paintThickness, t);
    const damageList = damages(v, t);
    const documentList = documents(v, t);
    const inspected = v.updated_at ? formatDay(v.updated_at) : '';

    return {
        reportDate: inspected,
        source: t('adapter.sourceInspection'),
        vehicle: {
            vin: (v.vin || '').toUpperCase(),
            brand: v.manufacturer,
            model: v.model || '',
            fields: vehicleFields(v, t),
        },
        pricing: pricing(v),
        // the condition summary: service book, owners and the accident flag (null when the inspection has no flag)
        condition: {
            serviceBook: v.serviceBookStatus || '',
            owners: present(v.ownersCount) ? Number(v.ownersCount) : null,
            accidents: v.flags && typeof v.flags.accidents_exists === 'boolean' ? v.flags.accidents_exists : null,
        },
        tiles: tiles(v, panels, damageList.length, documentList.length, t),
        status: [
            v.inspectionExpiry && {
                key: 'inspection',
                label: t('adapter.status.nextInspection'),
                value: v.inspectionExpiry,
                sub: v.lastInspection ? t('adapter.status.lastInspection', { date: v.lastInspection }) : '',
                source: 'inspection',
            },
        ].filter(Boolean),
        // on its own (Internal report) the page says where the data comes from; the Complete report has both sources
        notices: standalone
            ? [{ tone: 'info', title: t('adapter.notices.internalTitle'), text: t('adapter.notices.internalText') }]
            : [],
        inspection: {
            date: inspected,
            photos: (v.gallery || []).map((url, i) => ({ url, label: t('adapter.photo', { n: i + 1 }) })),
            paint: { panels },
            checks: checks(v, t),
            damages: damageList,
            documents: documentList,
            documentPhotos: documentPhotos(v, t),
            drive: driveNotes(v.testDriveNotes),
            videoUrl: v.videoUrl || '',
        },
        equipment: (v.equipment || []).length
            ? { source: t('adapter.equipmentInspection'), codes: v.equipment.map(name => ({ code: '', desc: name })) }
            : null,
    };
};
